import { awaitableTemplate } from '../../../../webmodule-common/other/ui/template-processor';
import { currencyCodeToString, moneyToTemplateResult } from '../../../../webmodule-common/other/currency-formatter';
import { customElement, property } from 'lit/decorators.js';
import { DashboardCache } from '../data/dashboard-cache';
import { DashboardWidget } from './dashboard-widget';

import { getApiFactory } from '../../../api/api-injector';
import { glossary } from '../../../../localization/global';
import { html } from 'lit';
import { LangDashboardBranchMonthlyRevenueByState } from '../../../../localization/scripts/dealer-franchisee/dashboard/widget/branch-monthly-revenue-bystate-lang';
import { PromiseSnippet } from '../../../../webmodule-common/interop/webmodule-interop';
import { QuoteState, ViewQuoteTotalByState } from '../../../api/dealer-api-interface-quote';
import { Snippet } from '../../../../webmodule-common/other/ui/events';

@customElement('dashboard-branch-monthly-revenue-bystate')
export class BranchMonthlyRevenueByState extends DashboardWidget {
  api = getApiFactory().quote();
  @property() dashboardCache: DashboardCache;

  @property() assignedToUserId?: string;
  @property() state = 0;

  protected async getData(): Promise<ViewQuoteTotalByState[]> {
    const state = this.state;
    return await this.dashboardCache.getMonthlyQuoteFinancials(state, this.assignedToUserId);
  }

  protected getHeader(): Snippet {
    return LangDashboardBranchMonthlyRevenueByState.monthlyGeneratedRevenue({ state: QuoteState[this.state] });
  }

  protected getQuoteLabel(): Snippet {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-enum-comparison
    if (this.state >= QuoteState.Approved) return glossary('order:plural', 'UC');
    return glossary('quote:plural', 'UC');
  }

  protected getBody(): Snippet {
    return html`${awaitableTemplate(this.getTemplate(), 'Loading...')}`;
  }

  protected async getTemplate(): PromiseSnippet {
    const data = await this.getData();

    // Group by currency (enum numeric), summing the count and total per currency.
    const groups = new Map<number, { quoteCount: number; total: number }>();
    data?.forEach(item => {
      const group = groups.get(item.currency) ?? { quoteCount: 0, total: 0 };
      group.quoteCount += item.quoteCount ?? 0;
      group.total += item.total ?? 0;
      groups.set(item.currency, group);
    });
    const currencies = [...groups.entries()];

    // No data: zero with default currency formatting.
    if (currencies.length === 0) {
      return html`
        <div class="card-subtitle text-secondary">0 ${this.getQuoteLabel()}</div>
        <div class="display-5 text-primary"></div>
      `;
    }

    // Single currency: keep the prominent KPI layout.
    if (currencies.length === 1) {
      const [currencyEnum, { quoteCount, total }] = currencies[0];
      return html`
        <div class="card-title fw-semibold">${currencyCodeToString(currencyEnum) ?? 'USD'}</div>
        <div class="card-subtitle text-secondary">${quoteCount} ${this.getQuoteLabel()}</div>
        <div class="display-5 text-primary">${moneyToTemplateResult(total, currencyEnum)}</div>
      `;
    }

    // Multiple currencies: one compact row each. The tile grows to fit a few,
    // then the body scrolls (.revenue-pipeline-* rules in dashboard_charts.scss),
    // instead of the full-size blocks overflowing the fixed-height card.
    return html`
      <div class="revenue-pipeline-currencies">
        ${currencies.map(
          ([currencyEnum, { quoteCount, total }]) => html`
            <div class="revenue-pipeline-currency">
              <span class="rpc-iso fw-semibold">${currencyCodeToString(currencyEnum) ?? 'USD'}</span>
              <span class="rpc-count text-secondary">${quoteCount} ${this.getQuoteLabel()}</span>
              <span class="rpc-amount text-primary">${moneyToTemplateResult(total, currencyEnum)}</span>
            </div>
          `
        )}
      </div>
    `;
  }
}
